import keys from 'lodash/keys'
import polylabel from 'polylabel'
import clustering from 'density-clustering'
import hull from 'hull.js'
import { max } from 'd3-array'
import stringWidth from './stringWidth'

const noop = () => null

const getContours = ({
  points,
  context,
  scale,
  fontSize,
  pixelsPerFoot,
  groupCluster,
  getGroupColor = noop,
  getGroupName = noop,
  getGroupId = noop,
  areaType,
}) => {
  const maxWidth = max(points, ({ w }) => w)
  const dbscan = new clustering.DBSCAN()
  const radius = pixelsPerFoot ? pixelsPerFoot * 7 : maxWidth * 6
  const clustersByArea = clusterByArea(dbscan, points, radius)

  let contours = []

  clustersByArea.forEach((cluster) => {
    // eslint-disable-next-line
    const { undefined: _, ...grouped } = groupCluster(cluster)

    keys(grouped).forEach((deptId) => {
      const subClusters = clusterByArea(dbscan, grouped[deptId], radius)

      subClusters.forEach((sc) => {
        if (sc.length > 0) {
          contours = [
            ...contours,
            getContour({
              group: sc,
              maxWidth,
              concavity: pixelsPerFoot * 6,
              getGroupColor,
              getGroupName,
              getGroupId,
              areaType,
            }),
          ]
        }
      })
    })
  })

  return updateLabelLocations({ contours, context, scale, fontSize })
}

const clusterByArea = (dbscan, points, r) =>
  dbscan
    .run(points, r, 1)
    .reduce((result, cluster) => [...result, cluster.map((i) => points[i])], [])

const getContour = ({
  group,
  maxWidth,
  concavity,
  getGroupColor,
  getGroupName,
  getGroupId,
  areaType,
}) => {
  const padding = maxWidth / 2

  const groupWithPadding = [
    ...group,
    ...group.reduce((acc, s) => {
      return acc.concat([
        [s[0] + padding, s[1] + padding],
        [s[0] - padding, s[1] - padding],
        [s[0] + padding, s[1] - padding],
        [s[0] - padding, s[1] + padding],
      ])
    }, []),
  ]

  const contour = hull(groupWithPadding, concavity || maxWidth * 6)
  contour.center = getCenter(contour)
  contour.color = getGroupColor(group)
  contour.name = getGroupName(group)
  contour.id = getGroupId(group)
  contour.areaType = areaType
  return contour
}

const getCenter = (points) => {
  const [x, y] = polylabel([points])
  return { x, y }
}

const updateLabelLocations = ({ contours, context, scale, fontSize }) => {
  let labelLocations = []

  contours.sort((c1, c2) => c1.center.x - c2.center.x)

  contours.forEach((c) => {
    const lWidth = stringWidth({
      text: c.name,
      fontSize: fontSize,
      context,
    })
    const maxY = max(c, (p) => p[1])
    const x1 = scale(c.center.x) - lWidth / 2
    const x2 = x1 + lWidth
    let y1 = scale(c.center.y) - fontSize / 2
    let y2 = y1 + fontSize
    let y = c.center.y

    labelLocations.forEach((l) => {
      if (
        (between(x1, l.x1, l.x2) ||
          between(x2, l.x1, l.x2) ||
          between(l.x1, x1, x2) ||
          between(l.x2, x1, x2)) &&
        (between(y1, l.y1, l.y2) ||
          between(y2, l.y1, l.y2) ||
          between(l.y1, y1, y2) ||
          between(l.y2, y1, y2))
      ) {
        y = max([y, scale.invert(l.y2)])
      }
    })

    if (y < maxY) {
      c.center.y = y
      y1 = scale(y) - fontSize / 2
      y2 = y1 + fontSize
      labelLocations = [...labelLocations, { x1, x2, y1, y2, name: c.name }]
    }
  })

  return contours
}

const between = (n, start, end) => n >= start && n <= end

export default getContours
